import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import * as LucideIcons from 'lucide-react';
import { toast } from 'sonner';

const TechnicalSpecs = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Simple password protection - in production, use proper authentication
  const TECHNICAL_ACCESS_PASSWORD = 'yardedge2026';

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    setTimeout(() => {
      if (password === TECHNICAL_ACCESS_PASSWORD) {
        setIsAuthenticated(true);
        toast.success('Access Granted', {
          description: 'Welcome to YardEdge Technical Documentation',
        });
      } else {
        toast.error('Access Denied', {
          description: 'Invalid password. Please contact us for access.',
        });
      }
      setIsLoading(false);
    }, 500);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-6">
        <Card className="max-w-md w-full bg-white/95 backdrop-blur-sm">
          <CardHeader className="text-center pb-4">
            <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
              <LucideIcons.Lock className="w-10 h-10 text-white" />
            </div>
            <CardTitle className="text-2xl">Protected Content</CardTitle>
            <CardDescription className="text-base">
              Technical specifications are restricted to verified investors and partners
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              <div>
                <label className="text-sm font-medium mb-2 block">Access Password</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="bg-white"
                  required
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-primary hover:bg-primary/90"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <LucideIcons.Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <LucideIcons.Unlock className="w-4 h-4 mr-2" />
                    Access Documentation
                  </>
                )}
              </Button>
            </form>
            <div className="mt-6 pt-6 border-t text-center">
              <p className="text-sm text-muted-foreground mb-3">
                Don't have access?
              </p>
              <Button 
                variant="outline" 
                onClick={() => window.location.href = '/#contact'}
                className="w-full"
              >
                <LucideIcons.Mail className="w-4 h-4 mr-2" />
                Request Access
              </Button>
            </div>
            <div className="mt-4 text-center">
              <Button 
                variant="ghost" 
                onClick={() => window.location.href = '/'}
                className="text-sm"
              >
                <LucideIcons.ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Protected content - Full technical specifications
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-md flex items-center justify-center">
                <LucideIcons.Zap className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-foreground">YardEdge</span>
              <Badge className="bg-red-500 text-white">CONFIDENTIAL</Badge>
            </div>
            <Button 
              variant="outline" 
              onClick={() => window.location.href = '/'}
            >
              <LucideIcons.Home className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-6 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
        <div className="container mx-auto max-w-6xl text-center">
          <Badge className="bg-red-500 text-white mb-4">PROPRIETARY & CONFIDENTIAL</Badge>
          <h1 className="text-5xl font-bold mb-4">
            YardEdge Technical Specifications
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Complete engineering blueprints for the 8'×6'×7' solar-powered mini data center
          </p>
          <a 
            href={`${process.env.REACT_APP_BACKEND_URL}/api/download/technical-specs`}
            download="YardEdge_Technical_Specifications.md"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-slate-900 hover:bg-gray-100 font-semibold rounded-md transition-colors"
          >
            <LucideIcons.Download className="w-5 h-5" />
            Download Complete Specifications
          </a>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          
          {/* Overview */}
          <Card className="mb-12 border-2 border-primary">
            <CardHeader>
              <CardTitle className="text-3xl flex items-center gap-3">
                <LucideIcons.Info className="w-8 h-8 text-primary" />
                System Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold text-foreground mb-1">8×6×7 ft</div>
                  <div className="text-sm text-muted-foreground">Dimensions (W×D×H)</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold text-foreground mb-1">IP65</div>
                  <div className="text-sm text-muted-foreground">Weather Rating</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold text-foreground mb-1">-20 to 50°C</div>
                  <div className="text-sm text-muted-foreground">Operating Temp</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold text-foreground mb-1">99.5%</div>
                  <div className="text-sm text-muted-foreground">Target Uptime</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Detailed Power System */}
          <Card className="mb-12">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-2xl">
                <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center">
                  <LucideIcons.Zap className="w-6 h-6 text-white" />
                </div>
                Power System Architecture
              </CardTitle>
              <CardDescription>Complete electrical specifications and component details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Solar Array */}
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <LucideIcons.Sun className="w-5 h-5 text-orange-500" />
                  Solar Panel Array
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Configuration:</span>
                    <span>16× 200W panels</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Peak Capacity:</span>
                    <span>3,200W (3.2kW)</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Mounting:</span>
                    <span>Roof-integrated, 10° tilt</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Panel Type:</span>
                    <span>Renogy 200W Monocrystalline</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">String Configuration:</span>
                    <span>240V DC series</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Current:</span>
                    <span>13.3A @ peak</span>
                  </div>
                </div>
              </div>

              {/* MPPT Controller */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <LucideIcons.Activity className="w-5 h-5 text-blue-500" />
                  Charge Controller
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Model:</span>
                    <span>Victron SmartSolar MPPT 150/70</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Max PV Input:</span>
                    <span>4,000W</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Efficiency:</span>
                    <span>98% peak</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Voltage Range:</span>
                    <span>150V max, 48V battery</span>
                  </div>
                </div>
              </div>

              {/* Battery System */}
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <LucideIcons.Battery className="w-5 h-5 text-green-500" />
                  Battery Bank
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Configuration:</span>
                    <span>2× SOK 48V 100Ah LiFePO4</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Total Capacity:</span>
                    <span>10.24 kWh</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Chemistry:</span>
                    <span>Lithium Iron Phosphate</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Autonomy:</span>
                    <span>2.9 days (no solar)</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Voltage Range:</span>
                    <span>42-58V DC</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Cable Size:</span>
                    <span>2/0 AWG</span>
                  </div>
                </div>
              </div>

              {/* Inverter */}
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <LucideIcons.Repeat className="w-5 h-5 text-purple-500" />
                  Inverter/Charger
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Model:</span>
                    <span>Victron MultiPlus-II 48/5000</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Continuous Output:</span>
                    <span>5kW</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Peak Output:</span>
                    <span>10kW (2 seconds)</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Grid Charger:</span>
                    <span>70A AC (backup)</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Transfer Switch:</span>
                    <span>Automatic</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Output:</span>
                    <span>120V/240V AC</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Computing System */}
          <Card className="mb-12">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-2xl">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <LucideIcons.Server className="w-6 h-6 text-white" />
                </div>
                Computing Infrastructure
              </CardTitle>
              <CardDescription>18U rack configuration and component specifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-bold text-lg mb-4">Server Specifications</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Model:</span>
                    <span>2× Intel NUC 13 Pro</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Processor:</span>
                    <span>Intel Core i7-1360P (per unit)</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Total Cores/Threads:</span>
                    <span>24C/32T combined</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Memory:</span>
                    <span>64GB DDR5 total (32GB each)</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Storage:</span>
                    <span>2TB NVMe SSD total</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Power Draw:</span>
                    <span>56W total (28W each)</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Network:</span>
                    <span>2× 2.5GbE ports per unit</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Performance:</span>
                    <span>~45,000 PassMark score</span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                <h4 className="font-bold text-lg mb-4">Networking Equipment</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Router:</span>
                    <span>UniFi Dream Machine Pro</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Primary Connection:</span>
                    <span>10Gbps fiber optic</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Backup Connection:</span>
                    <span>5G cellular (Netgear M6)</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Power Consumption:</span>
                    <span>35W</span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h4 className="font-bold text-lg mb-4">Monitoring & Management</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Controller:</span>
                    <span>Raspberry Pi 4 (8GB)</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">KVM Access:</span>
                    <span>Pi-KVM HAT</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Sensors:</span>
                    <span>6× Temperature (DS18B20)</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Additional Sensors:</span>
                    <span>Humidity, Smoke, Water leak</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Power Monitoring:</span>
                    <span>Emporia energy monitor</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white rounded">
                    <span className="font-medium">Power Draw:</span>
                    <span>15W</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cooling System */}
          <Card className="mb-12">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-2xl">
                <div className="w-10 h-10 bg-cyan-500 rounded-lg flex items-center justify-center">
                  <LucideIcons.Wind className="w-6 h-6 text-white" />
                </div>
                Thermal Management System
              </CardTitle>
              <CardDescription>Hybrid passive/active cooling architecture</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <Badge className="bg-green-500 text-white mb-3">Primary System</Badge>
                  <h4 className="font-bold mb-3">Passive Ventilation</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <LucideIcons.Check className="w-4 h-4 text-green-600 mt-0.5" />
                      <span>4× floor intake vents (8"×8" each)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <LucideIcons.Check className="w-4 h-4 text-green-600 mt-0.5" />
                      <span>4× roof exhaust vents (10"×10" each)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <LucideIcons.Check className="w-4 h-4 text-green-600 mt-0.5" />
                      <span>MERV-8 pleated filters (washable)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <LucideIcons.Check className="w-4 h-4 text-green-600 mt-0.5" />
                      <span>Natural convection ~150 CFM</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <LucideIcons.Check className="w-4 h-4 text-green-600 mt-0.5" />
                      <span>Effective when ambient &lt;25°C</span>
                    </li>
                  </ul>
                </div>

                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <Badge className="bg-blue-500 text-white mb-3">Active System</Badge>
                  <h4 className="font-bold mb-3">Forced Air Cooling</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <LucideIcons.Check className="w-4 h-4 text-blue-600 mt-0.5" />
                      <span>AC Infinity CLOUDPLATE T7-N</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <LucideIcons.Check className="w-4 h-4 text-blue-600 mt-0.5" />
                      <span>3× 120mm EC fans (PWM control)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <LucideIcons.Check className="w-4 h-4 text-blue-600 mt-0.5" />
                      <span>Max airflow: 280 CFM</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <LucideIcons.Check className="w-4 h-4 text-blue-600 mt-0.5" />
                      <span>Variable speed based on temperature</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <LucideIcons.Check className="w-4 h-4 text-blue-600 mt-0.5" />
                      <span>Power: 5-45W (variable)</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-bold mb-3">Heat Load Calculation</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="text-center p-3 bg-white rounded">
                    <div className="font-bold text-2xl text-foreground">191</div>
                    <div className="text-xs text-muted-foreground">BTU/hr Servers</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded">
                    <div className="font-bold text-2xl text-foreground">119</div>
                    <div className="text-xs text-muted-foreground">BTU/hr Network</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded">
                    <div className="font-bold text-2xl text-foreground">1,705</div>
                    <div className="text-xs text-muted-foreground">BTU/hr Solar Gain</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded">
                    <div className="font-bold text-2xl text-primary">2,083</div>
                    <div className="text-xs text-muted-foreground">BTU/hr Total</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Energy Budget */}
          <Card className="mb-12">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-2xl">
                <LucideIcons.TrendingUp className="w-8 h-8 text-green-600" />
                Energy Budget Analysis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold mb-4">Daily Energy Production (Average)</h4>
                  <div className="space-y-3">
                    <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-medium">Summer</span>
                        <span className="text-xl font-bold text-green-600">21.8 kWh/day</span>
                      </div>
                      <p className="text-xs text-muted-foreground">3.2kW × 8h × 0.85 efficiency</p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-medium">Winter</span>
                        <span className="text-xl font-bold text-blue-600">10.9 kWh/day</span>
                      </div>
                      <p className="text-xs text-muted-foreground">3.2kW × 4h × 0.85 efficiency</p>
                    </div>
                    <div className="p-3 bg-primary/10 rounded-lg border border-primary">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-medium">Annual Average</span>
                        <span className="text-xl font-bold text-primary">~16 kWh/day</span>
                      </div>
                      <p className="text-xs text-muted-foreground">~5,840 kWh/year</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-bold mb-4">Daily Energy Consumption</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between p-2 bg-gray-50 rounded">
                      <span>Servers (2× NUC, 24h)</span>
                      <span className="font-bold">1.34 kWh</span>
                    </div>
                    <div className="flex justify-between p-2 bg-gray-50 rounded">
                      <span>Networking (24h)</span>
                      <span className="font-bold">0.84 kWh</span>
                    </div>
                    <div className="flex justify-between p-2 bg-gray-50 rounded">
                      <span>Cooling (12h avg)</span>
                      <span className="font-bold">0.54 kWh</span>
                    </div>
                    <div className="flex justify-between p-2 bg-gray-50 rounded">
                      <span>Monitoring (24h)</span>
                      <span className="font-bold">0.36 kWh</span>
                    </div>
                    <div className="flex justify-between p-2 bg-gray-50 rounded">
                      <span>Inverter Loss (24h)</span>
                      <span className="font-bold">0.48 kWh</span>
                    </div>
                    <div className="flex justify-between p-3 bg-blue-100 rounded-lg border-2 border-blue-300 mt-2">
                      <span className="font-bold">Total Daily Consumption</span>
                      <span className="font-bold text-xl text-blue-600">3.56 kWh</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg border-2 border-green-300">
                <div className="text-center">
                  <h4 className="font-bold text-xl mb-2">Daily Energy Surplus</h4>
                  <div className="text-5xl font-bold text-green-600 mb-2">12.44 kWh/day</div>
                  <p className="text-muted-foreground">Available for grid export or neighbor sharing</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bill of Materials */}
          <Card className="mb-12">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-2xl">
                <LucideIcons.Package className="w-8 h-8 text-primary" />
                Bill of Materials (Prototype)
              </CardTitle>
              <CardDescription>Complete component list with costs</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-gray-300">
                      <th className="text-left p-2 font-bold">Category</th>
                      <th className="text-left p-2 font-bold">Item</th>
                      <th className="text-center p-2 font-bold">Qty</th>
                      <th className="text-right p-2 font-bold">Unit Cost</th>
                      <th className="text-right p-2 font-bold">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr className="bg-gray-50">
                      <td className="p-2 font-bold" rowSpan="3">Enclosure</td>
                      <td className="p-2">Cytech 18U IP65 Cabinet</td>
                      <td className="text-center p-2">1</td>
                      <td className="text-right p-2">$1,500</td>
                      <td className="text-right p-2 font-bold">$1,500</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="p-2">Concrete pad (materials)</td>
                      <td className="text-center p-2">1</td>
                      <td className="text-right p-2">$200</td>
                      <td className="text-right p-2">$200</td>
                    </tr>
                    <tr className="bg-gray-50 border-b-2">
                      <td className="p-2">Anchor bolts, mounting</td>
                      <td className="text-center p-2">1</td>
                      <td className="text-right p-2">$100</td>
                      <td className="text-right p-2">$100</td>
                    </tr>

                    <tr>
                      <td className="p-2 font-bold" rowSpan="5">Solar</td>
                      <td className="p-2">Renogy 200W panels</td>
                      <td className="text-center p-2">16</td>
                      <td className="text-right p-2">$180</td>
                      <td className="text-right p-2 font-bold">$2,880</td>
                    </tr>
                    <tr>
                      <td className="p-2">Roof mounting rails</td>
                      <td className="text-center p-2">1</td>
                      <td className="text-right p-2">$200</td>
                      <td className="text-right p-2">$200</td>
                    </tr>
                    <tr>
                      <td className="p-2">MC4 connectors</td>
                      <td className="text-center p-2">10</td>
                      <td className="text-right p-2">$3</td>
                      <td className="text-right p-2">$30</td>
                    </tr>
                    <tr>
                      <td className="p-2">Combiner box w/ breakers</td>
                      <td className="text-center p-2">1</td>
                      <td className="text-right p-2">$80</td>
                      <td className="text-right p-2">$80</td>
                    </tr>
                    <tr className="border-b-2">
                      <td className="p-2">PV cable (10AWG, 100ft)</td>
                      <td className="text-center p-2">1</td>
                      <td className="text-right p-2">$50</td>
                      <td className="text-right p-2">$50</td>
                    </tr>

                    <tr className="bg-gray-50">
                      <td className="p-2 font-bold" rowSpan="3">Power</td>
                      <td className="p-2">Victron MPPT 150/70</td>
                      <td className="text-center p-2">1</td>
                      <td className="text-right p-2">$600</td>
                      <td className="text-right p-2 font-bold">$600</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="p-2">SOK 48V 100Ah LiFePO4</td>
                      <td className="text-center p-2">2</td>
                      <td className="text-right p-2">$1,700</td>
                      <td className="text-right p-2">$3,400</td>
                    </tr>
                    <tr className="bg-gray-50 border-b-2">
                      <td className="p-2">Victron MultiPlus-II 48/5000</td>
                      <td className="text-center p-2">1</td>
                      <td className="text-right p-2">$1,800</td>
                      <td className="text-right p-2">$1,800</td>
                    </tr>

                    <tr>
                      <td className="p-2 font-bold" rowSpan="2">Computing</td>
                      <td className="p-2">Intel NUC 13 Pro i7/32GB</td>
                      <td className="text-center p-2">2</td>
                      <td className="text-right p-2">$1,200</td>
                      <td className="text-right p-2 font-bold">$2,400</td>
                    </tr>
                    <tr className="border-b-2">
                      <td className="p-2">UniFi Dream Machine Pro</td>
                      <td className="text-center p-2">1</td>
                      <td className="text-right p-2">$379</td>
                      <td className="text-right p-2">$379</td>
                    </tr>

                    <tr className="bg-primary/10 border-t-4 border-primary">
                      <td colSpan="4" className="p-3 text-right font-bold text-lg">TOTAL (Prototype):</td>
                      <td className="p-3 text-right font-bold text-2xl text-primary">$17,083</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Footer Warning */}
          <Card className="bg-red-50 border-2 border-red-300">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <LucideIcons.AlertTriangle className="w-8 h-8 text-red-600 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-lg text-red-900 mb-2">Confidentiality Notice</h4>
                  <p className="text-red-800">
                    This document contains proprietary and confidential information. Unauthorized disclosure, copying, 
                    distribution, or use of this information is strictly prohibited and may be unlawful. 
                    This information is provided solely for evaluation by authorized investors and partners.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

        </div>
      </section>
    </div>
  );
};

export default TechnicalSpecs;
